import EditAttributesIcon from '@mui/icons-material/EditAttributes'
import { Box, styled, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'

import { MButton, ProgressLoaders } from 'components'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useState } from 'react'
import { useMobileBreakpoints } from 'utils'
import { useSnackbar } from 'notistack'

import { UpdateCognitoDialog } from './UpdateCognitoDialog'

interface ICognitoDetailsProps {
  getUserData: () => Promise<void>
}

export const CognitoDetails = (props: ICognitoDetailsProps): JSX.Element => {
  const { getUserData } = props
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') ?? ''
  const { account, updateCognitoAttribute } = useAuth()
  const { enqueueSnackbar } = useSnackbar()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const isMobile = useMobileBreakpoints()

  const { escID, icID, defaultAccountId } = account || {}

  const displayItems = [
    {
      label: 'CCS-CSR- Impersonation - Account ID',
      cognitoLabel: 'custom:defaultAccountId',
      value: defaultAccountId
    },
    {
      label: 'MyHUB Customer Registered Account ID',
      cognitoLabel: 'custom:escID',
      value: escID
    },
    {
      label: 'Payment Vendor - Customer Account ID',
      cognitoLabel: 'custom:icID',
      value: icID
    }
  ]

  const [dialogOpen, setDialogOpen] = useState(false)

  const handleOpenDialog = (): void => { setDialogOpen(true) }
  const handleCloseDialog = (): void => { setDialogOpen(false) }

  const handleUpdateDialog = async (selected: string, value: string): Promise<void> => {
    setIsLoading(true)
    try {
      const param = {
        userName: email,
        attribName: selected,
        attribValue: value
      }
      const res = await updateCognitoAttribute(param)
      await getUserData()
      enqueueSnackbar(res.message, {
        variant: 'success',
        autoHideDuration: 3000
      })
    } catch (error) {
      enqueueSnackbar(`Failed to update ${selected}`, {
        variant: 'error',
        autoHideDuration: 3000
      })
    }
    setDialogOpen(false)
    setIsLoading(false)
  }

  if (isLoading) {
    return (
      <Box border={`1px solid ${color.secondary.dark[300]}`} p={2}>
        <ProgressLoaders height='100%' />
      </Box>
    )
  }
  return (
    <Box border={`1px solid ${color.secondary.dark[300]}`}>
      <Box
        display='flex'
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent='space-between'
        p={1}
        bgcolor={color.grey[50]}
        gap={isMobile ? 1 : 0}
      >
        <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
          <EditAttributesIcon
            sx={{
              color: color.grey[500],
              backgroundColor: color.grey[200],
              fontSize: 26,
              borderRadius: '50%',
              padding: 1
            }}
          />

          <Box
            display='flex'
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems='center'
            gap={1}
          >
            <Typography color={color.black} fontWeight={600} fontSize={16}>
              AWS Cognito
            </Typography>
          </Box>
        </Box>

        <Box display='flex' flexDirection={isMobile ? 'column' : 'row'} gap={1}>
          <MButton
            variant='outlined'
            type='button'
            rounded='true'
            texttransform='none'
            fullWidth={isMobile}
            onClick={handleOpenDialog}
          >
            Update
          </MButton>
        </Box>
      </Box>

      <Box px={2} py={2}>
        <Table>
          <TableContent>
            {displayItems.map((item: any, index: number) => (
              <TableItem key={index}>
                <ItemWrapper width='50%'>
                  <Typography color={color.grey[700]} fontSize={14}>
                    {item.label}
                  </Typography>
                </ItemWrapper>
                <ItemWrapper width='50%'>
                  <Typography color={color.grey[700]} fontSize={14}>
                    {item.value}
                  </Typography>
                </ItemWrapper>
              </TableItem>
            ))}
          </TableContent>
        </Table>
      </Box>
      <UpdateCognitoDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onUpdate={handleUpdateDialog}
        accountValues={{
          'custom:defaultAccountId': defaultAccountId,
          'custom:escID': escID,
          'custom:icID': icID
        }}
      />
    </Box>
  )
}

// interface ILinkedAccountsTableMobileView {
//   accountId: string
//   serviceAddress: string
// }

// const LinkedAccountsTableMobileView = (
//   props: ILinkedAccountsTableMobileView
// ): JSX.Element => {
//   return (
//     <Box
//       display='flex'
//       flexDirection='column'
//       p={2}
//       gap={2}
//       borderBottom={`solid 1px ${color.secondary.dark[100]}`}
//     >
//       <Box display='flex' flexDirection='column'>
//         <FormLabel textcolor={color.grey[600]}>Account Number</FormLabel>
//         <Typography color={color.grey[700]} fontSize={14}>
//           {props?.accountId}
//         </Typography>
//       </Box>

//       <Box display='flex' flexDirection='column'>
//         <FormLabel textcolor={color.grey[600]}>Service Address</FormLabel>
//         <Typography color={color.grey[700]} fontSize={14}>
//           {props?.serviceAddress}
//         </Typography>
//       </Box>
//     </Box>
//   )
// }

const Table = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  background: color.white
})

// const TableHeader = styled(Box)({
//   display: 'flex',
//   borderBottom: color.secondary.dark[300],
//   fontSize: 14,
//   fontWeight: 600,
//   paddingLeft: 20,
//   paddingRight: 20
// })

const TableContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 10
})

const TableItem = styled(Box)({
  display: 'flex',
  background: color.grey[100],
  borderBottom: color.secondary.dark[300],
  '&:hover': {
    backgroundColor: color.primary[50]
  }
})

const ItemWrapper = styled(Box)<{ width?: string }>(({ width }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  wordWrap: 'break-word',
  wordBreak: 'break-all',
  width,
  padding: 15,
  color: color.black,
  fontSize: 14,
  fontWeight: 400
}))
